import TaskService from "@/services/Tasks";
import type { TaskCreatePayload } from "@/services/Tasks";
import type {
    Task,
    PatchedTask,
    TaskStatusEnum,
} from "@/types/api/data-contracts";
import { ref } from "vue";

export interface TaskFilters {
    status?: TaskStatusEnum | TaskStatusEnum[];
    assignee_uuid?: string;
}

const hasStatusFilter = (status: TaskFilters["status"]): boolean =>
    Array.isArray(status) ? status.length > 0 : Boolean(status);

const selectedTask = ref<Task | null>(null);

export const useTasks = (props?: {
    targetType: string;
    targetUuid: string;
}) => {
    const loading = ref(false);
    const tasks = ref<Task[]>([]);

    const taskService = TaskService.getInstance();

    const fetchTasks = async (filters?: TaskFilters) => {
        if (!props?.targetUuid || !props?.targetType) return;

        loading.value = true;
        try {
            const res = await taskService.listEntityTasks({
                target_type: props.targetType,
                target_uuid: props.targetUuid,
                ...(hasStatusFilter(filters?.status) && {
                    status: filters!.status,
                }),
                ...(filters?.assignee_uuid && {
                    assignee_uuid: filters.assignee_uuid,
                }),
            });
            tasks.value = res.data?.results ?? [];
        } finally {
            loading.value = false;
        }
    };

    const createTask = async (
        payload: Omit<TaskCreatePayload, "target_type" | "target_uuid">
    ) => {
        if (!props?.targetUuid || !props?.targetType) return undefined;

        const res = await taskService.createEntityTask({
            ...payload,
            target_type: props.targetType,
            target_uuid: props.targetUuid,
        });
        return res.data;
    };

    const updateTask = async (taskUuid: string, payload: PatchedTask) => {
        // Echo target on PATCH: the BE serializer treats missing target_type/target_uuid
        // as "clear the target", which would detach company-scoped tasks from their entity.
        // Safe to always send when useTasks was scoped to a target.
        const targetEcho =
            props?.targetType && props?.targetUuid
                ? {
                      target_type: props.targetType,
                      target_uuid: props.targetUuid,
                  }
                : {};
        const res = await taskService.updateEntityTask(taskUuid, {
            ...payload,
            ...targetEcho,
        });
        return res.data;
    };

    const deleteTask = async (): Promise<boolean> => {
        if (!selectedTask.value?.uuid) return false;

        await taskService.deleteEntityTask(selectedTask.value.uuid);
        return true;
    };

    const setSelectedTask = (task: Task | null) => {
        selectedTask.value = task;
    };

    const clearSelection = () => {
        selectedTask.value = null;
    };

    return {
        loading,
        tasks,
        selectedTask,
        fetchTasks,
        createTask,
        updateTask,
        deleteTask,
        setSelectedTask,
        clearSelection,
    };
};
